import { type DialogProps, toast } from "@overmap-ai/blocks"
import { type Organization } from "@overmap-ai/common"
import { selectOrganizationById, selectUsersOfOrganization } from "@overmap-ai/core"
import { memo, useCallback, useMemo, useRef, useState } from "react"

import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { EmailVerificationModal } from "./EmailVerificationModal"

interface InviteToOrganizationDialogProps extends Omit<DialogProps, "title" | "content"> {
	organizationId: Organization["uuid"]
}

export const InviteToOrganizationDialog = memo((props: InviteToOrganizationDialogProps) => {
	const { organizationId, ...rest } = props

	const users = useShallowEqaulSelector(selectUsersOfOrganization(organizationId))
	const organization = useAppSelector(selectOrganizationById(organizationId))

	// If the organization requires single sign-on, the API grants membership instead of emailing an invite code.
	const description =
		organization?.sso_mode === "required"
			? `Membership is managed by your organization via SSO. The invitee gets an email and joins by signing in with ${organization.sso_provider_name ?? "SSO"}.`
			: undefined

	const [email, setEmail] = useState("")
	const [error, setError] = useState("")

	const existingEmails = useMemo(() => Object.values(users).map((user) => user.email), [users])

	// If single sign-on is required, the API response explains how the invitee joins.
	// The success toast shows that explanation.
	const inviteMessage = useRef("")

	const onInvite = useCallback(async () => {
		const result = await sdk.organizations.inviteWithResult(organizationId, email)
		inviteMessage.current = result?.message ?? ""
		return undefined
	}, [organizationId, email])

	const onSuccess = useCallback(
		() => toast.success({ title: "Invitation sent", description: inviteMessage.current }),
		[],
	)

	return (
		<EmailVerificationModal
			email={email}
			onEmailChange={setEmail}
			error={error}
			onErrorChange={setError}
			onConfirm={onInvite}
			onSuccess={onSuccess}
			title="Invite someone to this organization"
			description={description}
			emailsToFilterOut={existingEmails}
			emailToFilterOutErrorMessage="This user is already in the organization."
			{...rest}
		/>
	)
})
InviteToOrganizationDialog.displayName = "InviteToOrganizationDialog"
